import { useImperativeHandle, useState, type Ref } from 'react';
import { ChevronRight, CornerDownLeft } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@reflex/ui/components/ui/command';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import {
  matchFilterDefinitions,
  matchFilterOptions,
  parseFilterMentionScope,
} from './filter-mention';
import {
  filterValueLabel,
  isFilterApplied,
  selectableOptions,
  type FilterDefinition,
} from './filter-types';

/**
 * Keyboard control surface. The caret stays in the search box, so the input
 * drives the menu through this handle instead of the menu owning focus — the
 * same split the composer's mention menu uses.
 */
export interface FilterMentionHandle {
  moveUp: () => void;
  moveDown: () => void;
  /** Applies the highlighted row. False when there was nothing to apply. */
  selectHighlighted: () => boolean;
  /** Steps out of a scoped facet. False at the root, where Escape should close. */
  escape: () => boolean;
}

interface FilterMentionMenuProps {
  /** Every facet, applied or not: re-picking an applied one changes its value. */
  filters: FilterDefinition[];
  /** Token text after the `@`, e.g. `own` or `owner:ali`. */
  query: string;
  /** Commits a value for a facet. */
  onApply: (definition: FilterDefinition, value: string) => void;
  /** Narrows the token to one facet, rewriting it to `<id>:`. */
  onScopeFacet: (facetKey: string) => void;
  /** Drops the scope, rewriting the token back to a bare `@`. */
  onResetQuery: () => void;
  ref?: Ref<FilterMentionHandle>;
}

interface Row {
  /**
   * cmdk's row value, and what the highlight is tracked by. Prefixed with the
   * row index so it stays unique and case-insensitively distinct (cmdk lowers
   * values internally), and suffixed with the identity so the highlight
   * re-pins to the top whenever the row under it becomes a different thing.
   */
  key: string;
  testId: string;
  label: string;
  hint?: string;
  icon?: FilterDefinition['icon'];
  onSelect: () => void;
}

/**
 * The `@` menu for the search box: facets first, then that facet's values.
 *
 * It shows the same facets as the add-filter button, reached without leaving
 * the keyboard. Applied facets stay in the list with their current value as a
 * hint, since picking one again is how you change it.
 */
export function FilterMentionMenu({
  filters,
  query,
  onApply,
  onScopeFacet,
  onResetQuery,
  ref,
}: FilterMentionMenuProps) {
  const scope = parseFilterMentionScope(query);
  const scoped = scope ? (filters.find((f) => f.id === scope.facetKey) ?? null) : null;

  const rows: Row[] = buildRows();

  function buildRows(): Row[] {
    if (scoped && scope) {
      return matchFilterOptions(selectableOptions(scoped), scope.valueQuery).map(
        (option, index) => ({
          key: `${index}-${option.value}`.toLowerCase(),
          testId: `filter-mention-value-${option.value}`,
          label: option.label,
          onSelect: () => onApply(scoped, option.value),
        }),
      );
    }
    return matchFilterDefinitions(filters, query).map((definition, index) => ({
      key: `${index}-${definition.id}`.toLowerCase(),
      testId: `filter-mention-facet-${definition.id}`,
      label: definition.label,
      hint: isFilterApplied(definition) ? filterValueLabel(definition) : undefined,
      icon: definition.icon,
      onSelect: () => onScopeFacet(definition.id),
    }));
  }

  const [picked, setPicked] = useState('');
  // Derived, not stored: typing rewrites the list under the cursor, and a
  // highlight left pointing at a row that no longer exists would make Enter do
  // nothing. Whatever was picked wins while it survives; otherwise the top row.
  const highlight = rows.some((row) => row.key === picked) ? picked : (rows[0]?.key ?? '');

  const move = (direction: 1 | -1) => {
    if (rows.length === 0) return;
    const current = rows.findIndex((row) => row.key === highlight);
    const next = (current < 0 ? 0 : current + direction + rows.length) % rows.length;
    setPicked(rows[next].key);
  };

  useImperativeHandle(
    ref,
    () => ({
      moveUp: () => move(-1),
      moveDown: () => move(1),
      selectHighlighted: () => {
        const row = rows.find((r) => r.key === highlight) ?? rows[0];
        if (!row) return false;
        row.onSelect();
        return true;
      },
      escape: () => {
        if (!scoped) return false;
        onResetQuery();
        return true;
      },
    }),
    // Rebuilt per render on purpose: `rows` is derived from the live query, and
    // a stale handle would apply whatever was highlighted two keystrokes ago.
  );

  return (
    <Command shouldFilter={false} value={highlight} onValueChange={setPicked}>
      {scoped && (
        <div className="flex items-center gap-1.5 border-b border-border/50 px-2 py-1">
          <SectionLabel as="span">{scoped.label}</SectionLabel>
          <span className="ml-auto text-3xs text-muted-foreground/70">esc to go back</span>
        </div>
      )}
      <CommandList className="max-h-64">
        <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">
          No matches
        </CommandEmpty>
        <CommandGroup>
          {rows.map((row) => {
            const Icon = row.icon;
            return (
              <CommandItem
                key={row.key}
                value={row.key}
                onSelect={row.onSelect}
                className="gap-2 text-xs"
                data-testid={row.testId}
              >
                {Icon && (
                  <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                )}
                <span className={cn('truncate', row.hint ? 'shrink-0' : 'flex-1')}>
                  {row.label}
                </span>
                {row.hint && (
                  <span className="flex-1 truncate text-right text-muted-foreground">
                    {row.hint}
                  </span>
                )}
                {scoped ? (
                  <CornerDownLeft className="size-3 shrink-0 opacity-40" aria-hidden="true" />
                ) : (
                  <ChevronRight className="size-3.5 shrink-0 opacity-40" aria-hidden="true" />
                )}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
